// ahri_v5 — domain/filters.js

import { DEFAULT_SYSTEM_SHOP_FILTERS } from "../constants/defaultFilters.js";
import { FACET_UI_CACHE_STORAGE_PREFIX, FACET_UI_CACHE_TTL_MS, SYSTEM_SHOP_MATCH_TYPES, scopeStorageKey } from "../constants/widget.js";
import { normalizeSolrRowField } from "./solrDocs.js";

const SORT_ORDER_OPTIONS = ["asc", "desc", "count_asc", "count_desc", "custom"];
const VALUE_TYPE_OPTIONS = ["string", "number", "fraction"];

// Parses size-like strings such as "1/2 inch", "1-1/2\"", "2 1/2 in", "3 inch"
// into a plain decimal for numeric comparison -- handles whole numbers,
// simple fractions, and mixed numbers, with any unit suffix ignored. Returns
// NaN for anything that doesn't start with a recognizable number/fraction.
export function parseFractionValue(value) {
  const text = String(value ?? "").trim().toLowerCase();
  if (!text) return NaN;
  const match = text.match(
    /^(\d+(?:\.\d+)?)?\s*[-\s]\s*(\d+)\s*\/\s*(\d+)|^(\d+)\s*\/\s*(\d+)|^(\d+(?:\.\d+)?)/
  );
  if (!match) return NaN;
  if (match[2] !== undefined && match[3] !== undefined) {
    const whole = match[1] ? Number(match[1]) : 0;
    const denominator = Number(match[3]);
    return denominator ? whole + Number(match[2]) / denominator : whole;
  }
  if (match[4] !== undefined && match[5] !== undefined) {
    const denominator = Number(match[5]);
    return denominator ? Number(match[4]) / denominator : NaN;
  }
  if (match[6] !== undefined) return Number(match[6]);
  return NaN;
}

export function normalizeSystemShopMatchTypes(value, { includeUnmatched = false } = {}) {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(/[\s,|]+/)
      : [];
  const aliases = {
    complete_match: "complete",
    "complete-match": "complete",
    partial_match: "partial",
    "partial-match": "partial",
    unmatched: "none",
    no_match: "none",
    "no-match": "none",
  };
  const selected = rawValues
    .map((entry) => aliases[String(entry || "").trim()] || aliases[String(entry || "").trim().toLowerCase()] || String(entry || "").trim().toLowerCase())
    .filter((entry, index, list) => SYSTEM_SHOP_MATCH_TYPES.includes(entry) && list.indexOf(entry) === index);
  if (selected.length) {
    return SYSTEM_SHOP_MATCH_TYPES.filter((entry) => selected.includes(entry));
  }
  return includeUnmatched ? [...SYSTEM_SHOP_MATCH_TYPES] : ["complete", "partial"];
}

export function buildFacetUiCacheStorageKey(endpoint, authIdentity, accountId) {
  const endpointKey = String(endpoint || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const authKey = String(authIdentity || "").slice(-12).replace(/[^a-z0-9]+/gi, "_") || "anon";
  const baseKey = `${FACET_UI_CACHE_STORAGE_PREFIX}:${endpointKey || "default"}:${authKey}`;
  // Scoped by accountId in addition to the auth-derived suffix above: the
  // auth suffix alone collided across accounts sharing a backend/API key
  // (e.g. both falling back to the same flat token), leaking one account's
  // facet counts into another's cached view.
  return scopeStorageKey(baseKey, accountId);
}

export function readFacetUiCache(cacheKey) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(cacheKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const timestamp = Number(parsed?.timestamp || 0);
    if (!Number.isFinite(timestamp) || Date.now() - timestamp > FACET_UI_CACHE_TTL_MS) {
      window.localStorage.removeItem(cacheKey);
      return null;
    }
    return parsed?.payload && typeof parsed.payload === "object" ? parsed.payload : null;
  } catch {
    return null;
  }
}

export function writeFacetUiCache(cacheKey, payload) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      cacheKey,
      JSON.stringify({
        timestamp: Date.now(),
        payload,
      })
    );
  } catch {
    // ignore storage quota/access failures
  }
}

export function buildFilterConfigFingerprint(filters) {
  return JSON.stringify(
    (Array.isArray(filters) ? filters : []).map((filter) => ({
      key: filter?.key,
      groups: (Array.isArray(filter?.value_groups) ? filter.value_groups : []).map((group) => ({
        label: group?.label,
        values: [...(Array.isArray(group?.values) ? group.values : [])].map(String).sort(),
      })),
      showUngrouped: filter?.show_ungrouped_values !== false,
      sortOrder: filter?.sort_order || "asc",
      customOrder: Array.isArray(filter?.custom_value_order) ? filter.custom_value_order.map(String) : [],
    }))
  );
}

export function normalizeCachedFacetData(filters, cachedFacetData, cachedFingerprint) {
  const next = buildEmptyFilterState(filters);
  if (!cachedFacetData || typeof cachedFacetData !== "object") return next;
  // A cached entry holds already-grouped display rows from whatever filter
  // config was active when it was written -- if value_groups/show_ungrouped_values
  // has changed since then (e.g. an admin just added/edited a group), those rows
  // are stale display output, not raw facet counts, so they can't be re-grouped
  // here. Discard the whole cache rather than risk showing a stale or
  // double-grouped option list.
  if (cachedFingerprint !== undefined && cachedFingerprint !== buildFilterConfigFingerprint(filters)) {
    return next;
  }
  filters.forEach((filter) => {
    const rows = cachedFacetData?.[filter.key];
    if (!Array.isArray(rows)) return;
    next[filter.key] = rows
      .map((entry) => {
        const value = String(entry?.value || "").trim();
        if (!value) return null;
        return {
          value,
          count: parseOptionalCount(entry?.count),
        };
      })
      .filter(Boolean);
  });
  return next;
}

export function applyCachedAdvancedCounts(advancedFilters, cachedAdvancedCounts) {
  if (!cachedAdvancedCounts || typeof cachedAdvancedCounts !== "object") return advancedFilters;
  return (Array.isArray(advancedFilters) ? advancedFilters : []).map((filter) => {
    const countsForFilter = cachedAdvancedCounts?.[String(filter?.key || "")];
    if (!countsForFilter || typeof countsForFilter !== "object") return filter;
    const nextValues = (Array.isArray(filter?.values) ? filter.values : []).map((entry) => {
      const optionValue = String(entry?.value || entry?.label || "").trim();
      const countValue = parseOptionalCount(countsForFilter?.[optionValue]);
      return {
        ...entry,
        count: typeof countValue === "number" ? countValue : entry?.count ?? null,
      };
    });
    return {
      ...filter,
      values: nextValues,
    };
  });
}

export function buildAdvancedCountsSnapshot(advancedFilters) {
  const output = {};
  (Array.isArray(advancedFilters) ? advancedFilters : []).forEach((filter) => {
    const key = String(filter?.key || "").trim();
    if (!key) return;
    const optionCounts = {};
    (Array.isArray(filter?.values) ? filter.values : []).forEach((entry) => {
      const optionValue = String(entry?.value || entry?.label || "").trim();
      if (!optionValue) return;
      optionCounts[optionValue] = parseOptionalCount(entry?.count);
    });
    output[key] = optionCounts;
  });
  return output;
}

export function normalizeFilterIdentity(value) {
  return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export function normalizeSystemShopFilterOrder(value) {
  if (!Array.isArray(value)) return [];
  const normalized = [];
  const seen = new Set();
  value.forEach((entry) => {
    if (typeof entry === "string") {
      const key = String(entry || "").trim();
      const identity = `standard:${normalizeFilterIdentity(key)}`;
      if (!key || seen.has(identity)) return;
      seen.add(identity);
      normalized.push({ type: "standard", key });
      return;
    }
    if (!entry || typeof entry !== "object") return;
    const itemType = String(entry.type || "standard").trim().toLowerCase();
    if (itemType !== "standard" && itemType !== "advanced") return;
    const key = String(entry.key || entry.id || entry.name || entry.label || "").trim();
    const identity = `${itemType}:${normalizeFilterIdentity(key)}`;
    if (!identity || seen.has(identity)) return;
    seen.add(identity);
    normalized.push({ type: itemType, key });
  });
  return normalized;
}

export function parseValueGroups(raw) {
  const text = String(raw || "").trim();
  if (!text) return [];
  return text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.indexOf("=");
      if (idx === -1) return null;
      const label = line.slice(0, idx).trim();
      const values = line
        .slice(idx + 1)
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      return label && values.length ? { label, values } : null;
    })
    .filter(Boolean);
}

function normalizeValueGroups(entry) {
  const rawGroups = entry.value_groups || entry.value_groups_raw || [];
  if (Array.isArray(rawGroups)) {
    return rawGroups
      .filter((group) => group && typeof group === "object")
      .map((group) => {
        const label = String(group.label || "").trim();
        const rawValues = group.values || [];
        const values = Array.isArray(rawValues)
          ? rawValues.map((v) => String(v).trim()).filter(Boolean)
          : String(rawValues || "")
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean);
        return { label, values };
      })
      .filter((group) => group.label && group.values.length);
  }
  return parseValueGroups(rawGroups);
}

export function normalizeSystemShopFilters(value) {
  if (!Array.isArray(value)) {
    return DEFAULT_SYSTEM_SHOP_FILTERS.map((entry) => ({ ...entry, fields: [...entry.fields] }));
  }
  const normalized = value
    .filter((entry) => entry && typeof entry === "object")
    .map((entry) => {
      const key = String(entry.key || entry.id || entry.name || "").trim();
      const label = String(entry.label || entry.title || key).trim();
      const rawFields = entry.fields || entry.attributes || entry.attribute_fields || [];
      const fields = Array.isArray(rawFields)
        ? rawFields.map((field) => normalizeSolrRowField(field)).filter(Boolean)
        : String(rawFields || "")
            .split(/[\n,]+/)
            .map((field) => normalizeSolrRowField(field))
            .filter(Boolean);
      return {
        key,
        label: label || key,
        fields,
        value_type: VALUE_TYPE_OPTIONS.includes(String(entry.value_type || "").toLowerCase())
          ? String(entry.value_type).toLowerCase()
          : "string",
        sort_order: SORT_ORDER_OPTIONS.includes(String(entry.sort_order || "").toLowerCase())
          ? String(entry.sort_order).toLowerCase()
          : "asc",
        custom_value_order: Array.isArray(entry.custom_value_order)
          ? entry.custom_value_order.map((v) => String(v)).filter(Boolean)
          : [],
        value_groups: normalizeValueGroups(entry),
        show_ungrouped_values: entry.show_ungrouped_values !== false,
        multi_select: entry.multi_select === true || entry.multi_select === "true",
      };
    })
    .filter((entry) => entry.key && Array.isArray(entry.fields) && entry.fields.length);
  return normalized;
}

export function applyValueGroups(items, valueGroups, showUngrouped) {
  if (!Array.isArray(valueGroups) || !valueGroups.length) return items;
  const memberToGroupIndex = new Map();
  valueGroups.forEach((group, index) => {
    group.values.forEach((v) => memberToGroupIndex.set(String(v), index));
  });
  const groupBuckets = valueGroups.map((group) => ({ value: group.label, count: 0, _rank: Infinity }));
  const remaining = [];
  // `items` arrives already sorted by the filter's sort_order -- a group's
  // rank is its best (earliest-sorted) member's position, so bucket order
  // still follows sort_order instead of always falling back to the order
  // groups happen to be listed in on the admin's config screen.
  items.forEach((item, itemIndex) => {
    const groupIndex = memberToGroupIndex.get(String(item.value));
    if (groupIndex !== undefined) {
      const bucket = groupBuckets[groupIndex];
      bucket.count += typeof item.count === "number" ? item.count : 0;
      if (itemIndex < bucket._rank) bucket._rank = itemIndex;
    } else {
      remaining.push(item);
    }
  });
  const orderedGroupBuckets = groupBuckets
    .map((bucket, index) => ({ bucket, index }))
    .sort((a, b) => (a.bucket._rank !== b.bucket._rank ? a.bucket._rank - b.bucket._rank : a.index - b.index))
    .map(({ bucket: { _rank, ...rest } }) => rest);
  return showUngrouped !== false ? [...orderedGroupBuckets, ...remaining] : orderedGroupBuckets;
}

export function expandGroupedFilterValues(values, valueGroups) {
  if (!Array.isArray(valueGroups) || !valueGroups.length) return values;
  const groupMap = new Map(valueGroups.map((group) => [String(group.label), group.values]));
  const expanded = [];
  (values || []).forEach((value) => {
    const members = groupMap.get(String(value));
    if (members) {
      expanded.push(...members);
    } else {
      expanded.push(value);
    }
  });
  return [...new Set(expanded.map(String))];
}

export function parseAdvancedFilterValues(raw) {
  const text = String(raw || "").trim();
  if (!text) return [];
  return text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const idx = line.indexOf("=");
      if (idx === -1) {
        return { label: line, value: line, query: "" };
      }
      const label = line.slice(0, idx).trim();
      const query = line.slice(idx + 1).trim();
      return { label: label || query, value: label || query, query };
    })
    .filter((entry) => entry.label);
}

export function parseOptionalCount(value) {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export function normalizeSystemShopAdvancedFilters(value) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry) => entry && typeof entry === "object")
    .map((entry) => {
      const key = String(entry.key || entry.id || entry.name || "").trim();
      const label = String(entry.label || entry.title || key).trim() || key;
      const rawValues = entry.values || entry.values_raw || [];
      const values = Array.isArray(rawValues)
        ? rawValues
            .filter((item) => item && typeof item === "object")
            .map((item) => ({
              label: String(item.label || item.value || "").trim(),
              value: String(item.value || item.label || "").trim(),
              query: String(item.query || "").trim(),
              count: parseOptionalCount(item.count),
            }))
            .filter((item) => item.label)
        : parseAdvancedFilterValues(rawValues);
      return {
        key: key || label,
        label,
        values,
      };
    })
    .filter((entry) => entry.key);
}

export function buildOrderedSystemShopFilters(standardFilters, advancedFilters, order) {
  const standard = Array.isArray(standardFilters) ? standardFilters : [];
  const advanced = Array.isArray(advancedFilters) ? advancedFilters : [];
  const normalizedOrder = normalizeSystemShopFilterOrder(order);

  const byKey = new Map([
    ...standard.map((entry) => [`standard:${entry.key}`, { type: "standard", key: entry.key, label: entry.label, entry }]),
    ...advanced.map((entry) => [`advanced:${entry.key}`, { type: "advanced", key: entry.key, label: entry.label, entry }]),
  ]);

  const ordered = [];
  normalizedOrder.forEach((item) => {
    const id = `${item.type}:${item.key}`;
    if (!byKey.has(id)) return;
    ordered.push(byKey.get(id));
    byKey.delete(id);
  });
  return [...ordered, ...Array.from(byKey.values())];
}

export function buildEmptyFilterState(filters) {
  return filters.reduce((acc, filter) => {
    acc[filter.key] = [];
    return acc;
  }, {});
}

export function hasAnySelectedFilterValue(selection) {
  if (!selection || typeof selection !== "object") return false;
  return Object.values(selection).some((values) => Array.isArray(values) && values.length > 0);
}

export function extractFacetItems(rawFacet) {
  if (!Array.isArray(rawFacet)) return [];
  return rawFacet
    .map((row) =>
      typeof row === "object" && row !== null
        ? { value: row.value, count: typeof row.count === "number" ? row.count : null }
        : { value: row, count: null }
    )
    .filter((entry) => entry.value !== null && entry.value !== undefined && entry.value !== "");
}

export function extractProductAttrFacetItems(rawFacet) {
  if (!rawFacet) return [];
  if (Array.isArray(rawFacet?.values)) {
    return rawFacet.values
      .map((row) =>
        typeof row === "object" && row !== null
          ? { value: row.value, count: typeof row.count === "number" ? row.count : null }
          : { value: row, count: null }
      )
      .filter((entry) => entry.value !== null && entry.value !== undefined && entry.value !== "");
  }
  if (Array.isArray(rawFacet)) {
    return rawFacet
      .map((row) =>
        typeof row === "object" && row !== null
          ? { value: row.value, count: typeof row.count === "number" ? row.count : null }
          : { value: row, count: null }
      )
      .filter((entry) => entry.value !== null && entry.value !== undefined && entry.value !== "");
  }
  if (typeof rawFacet === "string" || typeof rawFacet === "number") {
    return [{ value: rawFacet, count: null }];
  }
  return [];
}

export function sortFacetItems(items, sortOrder, valueType, customOrder) {
  const map = new Map();
  items.forEach((item) => {
    if (item?.value === null || item?.value === undefined || item?.value === "") return;
    const key = String(item.value);
    const prev = map.get(key);
    const nextCount = typeof item.count === "number" ? item.count : prev?.count ?? null;
    map.set(key, { value: item.value, count: nextCount });
  });
  const deduped = Array.from(map.values());
  const byValueAsc = (a, b) =>
    String(a.value).localeCompare(String(b.value), undefined, { numeric: true, sensitivity: "base" });

  if (sortOrder === "count_asc" || sortOrder === "count_desc") {
    const dir = sortOrder === "count_desc" ? -1 : 1;
    return deduped.sort((a, b) => {
      const aCount = typeof a.count === "number" ? a.count : 0;
      const bCount = typeof b.count === "number" ? b.count : 0;
      return aCount !== bCount ? dir * (aCount - bCount) : byValueAsc(a, b);
    });
  }

  if (sortOrder === "custom") {
    const indexMap = new Map((Array.isArray(customOrder) ? customOrder : []).map((v, i) => [String(v), i]));
    return deduped.sort((a, b) => {
      const aIdx = indexMap.has(String(a.value)) ? indexMap.get(String(a.value)) : Infinity;
      const bIdx = indexMap.has(String(b.value)) ? indexMap.get(String(b.value)) : Infinity;
      return aIdx !== bIdx ? aIdx - bIdx : byValueAsc(a, b);
    });
  }

  if (valueType === "number" || valueType === "fraction") {
    // parseFractionValue also parses plain decimals identically to Number()
    // (and additionally parses "1/2"/"1-1/2"-style fractions), so a "number"
    // field with a mix of decimal and fraction values still sorts by actual
    // size instead of a fraction string parsing as NaN.
    return deduped.sort((a, b) => {
      const aNum = parseFractionValue(a.value);
      const bNum = parseFractionValue(b.value);
      const aValid = Number.isFinite(aNum);
      const bValid = Number.isFinite(bNum);
      if (!aValid && !bValid) return byValueAsc(a, b);
      if (!aValid) return 1;
      if (!bValid) return -1;
      return sortOrder === "desc" ? bNum - aNum : aNum - bNum;
    });
  }
  return deduped.sort((a, b) => (sortOrder === "desc" ? -byValueAsc(a, b) : byValueAsc(a, b)));
}

export function processFacets(facets, productAttrFacets, filterConfig) {
  const output = {};
  filterConfig.forEach((filter) => {
    const items = [];
    (filter.fields || []).forEach((field) => {
      if (!field) return;
      if (String(field).startsWith("prodattr_")) {
        items.push(...extractProductAttrFacetItems(productAttrFacets?.[field]));
      } else {
        items.push(...extractFacetItems(facets?.[field]));
      }
    });
    const sorted = sortFacetItems(items, filter.sort_order || "asc", filter.value_type || "string", filter.custom_value_order);
    output[filter.key] = applyValueGroups(sorted, filter.value_groups, filter.show_ungrouped_values !== false);
  });
  return output;
}

export function quoteValue(value, isNumeric) {
  if (isNumeric) return String(value);
  return `"${String(value).replace(/"/g, '\\"')}"`;
}

// Must match backend `_solr_facet_exclusion_tag` (backend/api/ahri.py) exactly --
// the tag here is what lets the backend exclude this field's own fq from its
// own facet counts, so a shopper can still see (and add) sibling values of a
// field they've already filtered on instead of every other value collapsing
// to zero results.
function solrFacetExclusionTag(field) {
  const safe = String(field || "").replace(/[^A-Za-z0-9_]/g, "_");
  return `flt_${safe}`;
}

export function buildFieldClause(fields, values, isNumeric) {
  if (!Array.isArray(fields) || !fields.length) return "";
  if (!Array.isArray(values) || !values.length) return "";
  const valueExpr = values.map((value) => quoteValue(value, isNumeric)).join(" OR ");
  const clauses = fields.map((field) => `${field}:(${valueExpr})`);
  const combined = clauses.length === 1 ? clauses[0] : `(${clauses.join(" OR ")})`;
  // `{!tag=}` local params are only valid as the very first token of the whole
  // fq string -- they can't be repeated mid-expression inside an OR'd clause --
  // so a multi-field filter gets ONE tag prefix listing every field's tag
  // (Solr's `tag` local param accepts a comma-separated list). Each field's
  // own facet request excludes just its own tag name via `{!ex=}`, which still
  // matches here since that tag name is present in the shared list.
  const tags = fields.map((field) => solrFacetExclusionTag(field)).join(",");
  return `{!tag=${tags}}${combined}`;
}

export function buildFilterPanelKey(filterType, filterKey) {
  const normalizedType = String(filterType || "standard").trim().toLowerCase() === "advanced" ? "advanced" : "standard";
  const normalizedKey = String(filterKey || "").trim();
  return normalizedKey ? `${normalizedType}:${normalizedKey}` : "";
}

export function resolveMobileActiveFilterKey(panelKeys, preferredKey = "") {
  const normalizedKeys = (Array.isArray(panelKeys) ? panelKeys : [])
    .map((entry) => String(entry || "").trim())
    .filter(Boolean);
  if (!normalizedKeys.length) return "";
  const preferred = String(preferredKey || "").trim();
  if (preferred && normalizedKeys.includes(preferred)) return preferred;
  return normalizedKeys[0];
}

export function normalizeProductCodeFilters(value) {
  if (Array.isArray(value)) {
    return [...new Set(value.map((entry) => String(entry || "").trim()).filter(Boolean))];
  }
  const raw = String(value || "").trim();
  if (!raw) return [];
  return [...new Set(raw.split(/[,\s]+/).map((entry) => String(entry || "").trim()).filter(Boolean))];
}

export function areStringListsEqual(left, right) {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (String(left[index]) !== String(right[index])) return false;
  }
  return true;
}
